import type {
  AdminGuide,
  GuideAudience,
  GuideCategory,
  Risk,
} from "./adminGuides.types";
/**
 * Guides Library registry (source of truth).
 *
 * @remarks
 * - Keep `AdminGuide.id` stable because it is used for deep links: `/admin/guides/:guideId`.
 * - Guide content must remain safe: no secrets/tokens/PII (see `docs/LOGGING.md` for workspace safety norms).
 * - Maintain the docs-first proof and workflow mapping in:
 *   `apps/admin/src/guides/ADMIN_GUIDES_INVENTORY.md`.
 */
import { accountantReengagementSupportGuide } from "./guides/accountantReengagementSupportGuide";
import { accountingMethodCashVsAccrualGuide } from "./guides/accountingMethodCashVsAccrualGuide";
import { billingStripeWhichLinkGuide } from "./guides/billingStripeWhichLinkGuide";
import { bugReportingGuide } from "./guides/bugReportingGuide";
import { bugReportInvestigationGuide } from "./guides/bugReportInvestigationGuide";
import { bulkEmailGuide } from "./guides/bulkEmailGuide";
import { crmDuplicatesAndIdentityReviewGuide } from "./guides/crmDuplicatesAndIdentityReviewGuide";
import { demoAccessGuide } from "./guides/demoAccessGuide";
import { demoOpsDiagnosticsGuide } from "./guides/demoOpsDiagnosticsGuide";
import { exportUsersCsvGuide } from "./guides/exportUsersCsvGuide";
import { liveAlertsRulesGuide } from "./guides/liveAlertsRulesGuide";
import { onboardingSupportGuide } from "./guides/onboardingSupportGuide";
import { partnerPortalInvitesGuide } from "./guides/partnerPortalInvitesGuide";
import { providerResyncAndOverridesGuide } from "./guides/providerResyncAndOverridesGuide";
import { spoofingSafelyGuide } from "./guides/spoofingSafelyGuide";
import { usageAnalyticsGuide } from "./guides/usageAnalyticsGuide";

export const ADMIN_GUIDES: AdminGuide[] = [
  spoofingSafelyGuide,
  bugReportingGuide,
  bugReportInvestigationGuide,
  liveAlertsRulesGuide,
  billingStripeWhichLinkGuide,
  onboardingSupportGuide,
  accountingMethodCashVsAccrualGuide,
  providerResyncAndOverridesGuide,
  demoAccessGuide,
  demoOpsDiagnosticsGuide,
  usageAnalyticsGuide,
  exportUsersCsvGuide,
  bulkEmailGuide,
  accountantReengagementSupportGuide,
  // Sprint 3 links here from duplicates, users, organisations, and contact
  // drilldowns, so removing this registration would strand multiple help CTAs.
  crmDuplicatesAndIdentityReviewGuide,
  partnerPortalInvitesGuide,
];

export const getAdminGuideById = (guideId: string): AdminGuide | undefined => {
  return ADMIN_GUIDES.find((guide) => guide.id === guideId);
};

export const getGuideCategories = (): GuideCategory[] => {
  const categories = new Set<GuideCategory>();
  for (const guide of ADMIN_GUIDES) {
    categories.add(guide.category);
  }
  return Array.from(categories).sort((a, b) => a.localeCompare(b));
};

export const getGuideAudiences = (): GuideAudience[] => {
  const audiences = new Set<GuideAudience>();
  for (const guide of ADMIN_GUIDES) {
    for (const audience of guide.audience) {
      audiences.add(audience);
    }
  }
  return Array.from(audiences).sort((a, b) => a.localeCompare(b));
};

export const getGuideRisks = (): Risk[] => {
  return ["low", "medium", "high"];
};
